import { Button, DropDownInput, SelectMode, CheckBoxInput } from "@/components";
import { useState, useMemo } from "react";
import { AiOutlineCheck } from "react-icons/ai";
import { useSelector } from "react-redux";
import { selectAllPickupAddresses } from "../../app/slices";
import { useAlert, useApi } from "@/";
import { Link } from "react-router";
import { ServiceabilityTable } from "../../components";
const BookedOrderForm = ({
  setSheetOpen,
  handelPlaceOrder = () => {},
  isLoading = false,
  orders = [],
}) => {
  const { alert, AlertDialog } = useAlert();
  const { callApi, error, loading } = useApi();
  const [data, setData] = useState([]);
  const user = useSelector((state) => state.user?.user) || {};
  const [codCol, setCodCol] = useState("Prepaid");
  const courierSettings = useSelector(
    (state) => state.setting?.settings?.courierManagement,
  );
  const customerConfig =
    useSelector((state) => state.setting?.settings?.courierManagement) || [];
  const couriers =
    useSelector((state) => state.app?.appData?.courier)?.filter((courier) => {
      if (!courier.isActive) return false;

      // If no customer config exists, show all active couriers
      if (customerConfig.length === 0) return true;

      // Find the config for this courier
      const config = customerConfig.find(
        (config) => config.courierId === courier._id,
      );

      // If no config exists for this courier, it means it's a new courier - show it by default
      // If config exists, only show if it's enabled
      return config ? config.isEnabled : true;
    }) || [];
  const [formData, setFormData] = useState({
    courierPartner: "",
    mode: "",
    isEssentialItems: false,
    isDangerousGoods: false,
    pickupAddressId: "",
  });

  const options = useMemo(() => {
    return couriers.map((courier) => ({
      label: courier.name,
      value: courier._id + "." + courier.name,
    }));
  }, [couriers]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const pickupAddresses = useSelector(selectAllPickupAddresses) || [];
  const option = pickupAddresses.map((address) => ({
    label: `${address.contactPerson} (${address.pincode})`,
    value: address._id,
  }));
  const modeOptions = useMemo(
    () => [
      { label: "Air Mode", value: "Air" },
      { label: "Surface Mode", value: "Surface" },
    ],
    [],
  );

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent form default submission
    if (
      !formData.courierPartner ||
      !formData.mode ||
      !formData.pickupAddressId
    ) {
      await alert(
        "Please fill in all required fields: Pickup Address, Courier Partner, and Mode.",
      );
      return;
    }

    try {
      await handelPlaceOrder(formData);
    } catch (error) {
      await alert("Failed to place order: " + error.message);
    }
  };

  const handleFetchPricing = async () => {
    if (
      !formData.pickupAddressId ||
      !formData.mode ||
      !formData.courierPartner
    ) {
      await alert(
        "Please select a pickup address, courier partner and service mode first.",
      );
      return;
    }
    const address = pickupAddresses.find(
      (address) => address._id === formData.pickupAddressId,
    );
    console.log(address);
    try {
      const order = orders[0];
      const data = {
        pickupPincode: address?.pincode,
        deliveryPincode: order?.deliveryAddress?.pincode,
        weight: Math.max(0.5, order?.physicalWeight || 0),
        length: order?.dimensions?.length,
        breadth: order?.dimensions?.breadth,
        height: order?.dimensions?.height,
        paymentMode: order?.paymentMode,
        serviceMode: formData?.mode,
        shipmentValue: order?.totalOrderValue,
      };

      const response = await callApi("common/rate-calculator", "GET", null, {
        ...data,
        userId: user?._id,
      });

      console.log("Response from API:", response);
      if (response?.success && Array.isArray(response?.data?.rates)) {
        setCodCol(response?.paymentMode);
        // Get enabled courier IDs from settings
        /* const enabledCourierIds = new Set(
          courierSettings
            ?.filter((setting) => setting.isEnabled)
            ?.map((setting) => setting.courierId) || []
        ); */
        const enabledCourierIds = new Set(
          courierSettings
            ?.filter(
              (setting) =>
                setting.courierId === formData?.courierPartner.split(".")[0],
            )
            ?.map((setting) => setting.courierId) || [],
        );

        // Create a map of courier names to IDs
        const courierNameToId = {};
        couriers?.forEach((courier) => {
          courierNameToId[courier.name] = courier._id;
        });

        const formattedData = response?.data?.rates
          ?.map((rate) => ({
            courierName: rate?.courierName,
            courierId: courierNameToId[rate?.courierName],
            shippingCharge: rate?.zonePrice,
            codCharges:
              order?.paymentMode === "COD"
                ? Number(rate?.codCharges?.toFixed(2))
                : null,
            gstCharges: Number(
              (
                (rate?.zonePrice +
                  (order?.paymentMode === "COD" ? rate?.codCharges : 0)) *
                rate?.gst
              ).toFixed(2),
            ),
            totalCharge: Number(
              (
                rate.zonePrice +
                (order?.paymentMode === "COD" ? rate?.codCharges : 0) +
                (rate?.zonePrice + rate?.codCharges) * rate?.gst
              ).toFixed(2),
            ),
          }))
          ?.filter((rate) => {
            // Only show rates for enabled couriers
            return (
              enabledCourierIds.size === 0 ||
              enabledCourierIds.has(rate.courierId)
            );
          });

        console.log("formattedData", formattedData);
        setCodCol(response?.data?.paymentMode);
        // Prevent redundant state updates
        setData((prevData) =>
          JSON.stringify(prevData) === JSON.stringify(formattedData)
            ? prevData
            : formattedData,
        );
      } else {
        setData([]);
        console.error(
          "Rates data is not available in the response: ",
          response.error,
        );
      }
    } catch (err) {
      console.error("Error during API call:", err);
    }
  };
  const columns = useMemo(
    () => [
      "Courier Name",
      "Shipping Charge",
      ...(codCol === "COD" ? ["COD Charges"] : []),
      "GST Charges",
      "Total Charge",
    ],
    [codCol], // Dependency ensures columns update when codCol changes
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col justify-start gap-8 mt-4"
    >
      {/* Step 3: Pickup Address Options */}

      <DropDownInput
        label="Pickup Address"
        placeholder="Select Pickup Address"
        options={option}
        value={formData.pickupAddressId}
        onChange={(value) => handleChange("pickupAddressId", value)}
      />
      {/* Step 1: Courier Selection */}
      <DropDownInput
        label="Courier Partner"
        placeholder="Select Courier Partner"
        options={options}
        value={formData.courierPartner}
        onChange={(value) => handleChange("courierPartner", value)}
        optionClassName="hover:bg-primary/10 cursor-pointer"
        selectedClassName="bg-primary/10"
      />

      {/* Step 2: Mode Selection */}
      <SelectMode
        label="Select Mode"
        name="mode"
        options={modeOptions}
        selectedValue={formData.mode}
        onChange={(value) => handleChange("mode", value)}
        selectedClassName="bg-primary/10"
      />

      {/* Step 4: Additional Options */}
      <div className="flex flex-col gap-2 pl-2">
        <CheckBoxInput
          label="Check this if order contains essential items."
          checked={formData.isEssentialItems}
          labelClassName="text-body2 font-ptsans"
          onChange={(value) => handleChange("isEssentialItems", value)}
        />

        <CheckBoxInput
          label="Check this if order contains dangerous goods."
          checked={formData.isDangerousGoods}
          labelClassName="text-body2 font-ptsans"
          onChange={(value) => handleChange("isDangerousGoods", value)}
        />
      </div>
      {console.log("orders::::>>", orders)}
      {orders.length === 1 && (
        <div className="w-full p-2 bg-background_2 rounded-xl flex justify-end flex-col">
          <Button
            variant="default"
            size="xs"
            type="button"
            onClick={handleFetchPricing}
          >
            Fetch Pricing
          </Button>

          {data.length > 0 && (
            <ServiceabilityTable
              columns={columns}
              data={data}
              paymentMode={codCol}
              isPercentage={false}
              isCurrency={true}
            />
          )}
        </div>
      )}

      {/* Footer Actions */}
      <div className="flex gap-2 justify-end items-center">
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => setSheetOpen(false)}
        >
          Cancel
        </Button>
        <Link to={`/settings/pickupAddress`}>
          <Button variant="default" size="sm" type="button">
            Add Pickup
          </Button>
        </Link>
        <Button
          variant="default"
          size="sm"
          icon={AiOutlineCheck}
          type="submit"
          isLoading={isLoading}
          loadingText="Placing Order..."
        >
          Place Order
        </Button>
      </div>
      <AlertDialog />
    </form>
  );
};

export default BookedOrderForm;
